import { useEffect, useRef, useState } from 'react';
import { Link } from 'wouter';
import { useTranslation } from 'react-i18next';
import { localizeHref } from '@/lib/marketing/localizedLandings';
import { Menu, X, ChevronDown, Stethoscope, Building2, Sparkles, Scissors, Brush, Dumbbell, Target, CircleDot, HeartHandshake, Users, NotebookText, Lock, ArrowRight, Plus } from 'lucide-react';
import { useAuth } from '@/contexts/auth-context';
import { SEOHead } from '@/pages/landing/shared/SEOHead';
import { PricingTable } from '@/components/subscription/PricingTable';
import { LanguageSelector } from '@/components/LanguageSelector';
import { useDetectedCurrency } from '@/hooks/useCurrency';

const SOLUTIONS = [
  {
    labelKey: 'home:solutions.healthcare.label',
    items: [
      { labelKey: 'home:solutions.healthcare.items.doctors', href: '/for-doctors', Icon: Stethoscope },
      { labelKey: 'home:solutions.healthcare.items.clinics', href: '/for-clinics', Icon: Building2 },
      { labelKey: 'home:solutions.healthcare.items.spa', href: '/for-spa', Icon: Sparkles },
      { labelKey: 'home:solutions.healthcare.items.therapists', href: '/for-therapists', Icon: HeartHandshake },
    ],
  },
  {
    labelKey: 'home:solutions.beauty.label',
    items: [
      { labelKey: 'home:solutions.beauty.items.salons', href: '/for-salons', Icon: Scissors },
      { labelKey: 'home:solutions.beauty.items.barbers', href: '/for-barbers', Icon: Scissors },
      { labelKey: 'home:solutions.beauty.items.beautyPros', href: '/for-beauty', Icon: Brush },
    ],
  },
  {
    labelKey: 'home:solutions.fitness.label',
    items: [
      { labelKey: 'home:solutions.fitness.items.trainers', href: '/for-trainers', Icon: Dumbbell },
      { labelKey: 'home:solutions.fitness.items.smallGyms', href: '/for-small-gyms', Icon: Users },
      { labelKey: 'home:solutions.fitness.items.coaches', href: '/for-coaches', Icon: Target },
      { labelKey: 'home:solutions.fitness.items.golfTeachers', href: '/for-golf-teachers', Icon: CircleDot },
    ],
  },
];

// Compact cards in the "Built for how you work" router. Titles reuse the nav's
// solutions labels; the hook is the one thing that vertical does differently.
const ROUTER_COMPACT = [
  { slug: 'salons', href: '/for-salons', Icon: Scissors, labelKey: 'home:solutions.beauty.items.salons', hookKey: 'home:router.compact.salons' },
  { slug: 'barbers', href: '/for-barbers', Icon: Scissors, labelKey: 'home:solutions.beauty.items.barbers', hookKey: 'home:router.compact.barbers' },
  { slug: 'clinics', href: '/for-clinics', Icon: Building2, labelKey: 'home:solutions.healthcare.items.clinics', hookKey: 'home:router.compact.clinics' },
  { slug: 'trainers', href: '/for-trainers', Icon: Dumbbell, labelKey: 'home:solutions.fitness.items.trainers', hookKey: 'home:router.compact.trainers' },
  { slug: 'coaches', href: '/for-coaches', Icon: Target, labelKey: 'home:solutions.fitness.items.coaches', hookKey: 'home:router.compact.coaches' },
];

const SITE_URL = 'https://slotyapp.com';

const ROTATING_WORDS_FALLBACK = [
  'cutting hair',
  'training',
  'in session',
  'consulting',
  'coaching',
  'sleeping',
];

export default function HomePage() {
  const { t, i18n } = useTranslation(['home', 'common']);
  const { isAuthenticated, user } = useAuth();
  const { currency } = useDetectedCurrency();
  const dashboardUrl = user?.role === 'staff' ? '/staff/dashboard' : '/admin/dashboard';

  const rotatingWordsRaw = t('home:hero.rotatingWords', { returnObjects: true });
  const rotatingWords: string[] = Array.isArray(rotatingWordsRaw)
    ? (rotatingWordsRaw as string[])
    : ROTATING_WORDS_FALLBACK;
  const proofCategoriesRaw = t('home:proof.categories', { returnObjects: true });
  const proofCategories: string[] = Array.isArray(proofCategoriesRaw)
    ? (proofCategoriesRaw as string[])
    : [];

  const [wordIdx, setWordIdx] = useState(0);
  const [fading, setFading] = useState(false);
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSolutionsOpen, setMobileSolutionsOpen] = useState(false);
  const solutionsCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openSolutions = () => {
    if (solutionsCloseTimeoutRef.current) {
      clearTimeout(solutionsCloseTimeoutRef.current);
      solutionsCloseTimeoutRef.current = null;
    }
    setSolutionsOpen(true);
  };
  const scheduleCloseSolutions = () => {
    solutionsCloseTimeoutRef.current = setTimeout(() => setSolutionsOpen(false), 150);
  };
  useEffect(() => () => {
    if (solutionsCloseTimeoutRef.current) clearTimeout(solutionsCloseTimeoutRef.current);
  }, []);

  useEffect(() => {
    let swap: ReturnType<typeof setTimeout>;
    const tick = setInterval(() => {
      setFading(true);
      swap = setTimeout(() => {
        setWordIdx((i) => (i + 1) % rotatingWords.length);
        setFading(false);
      }, 500);
    }, 3000);
    return () => {
      clearInterval(tick);
      clearTimeout(swap);
    };
  }, [rotatingWords.length]);

  return (
    <div className="lp-a-root">
      <SEOHead
        title={t('common:seo.home.title')}
        description={t('common:seo.home.description')}
        canonical={SITE_URL}
        keywords={t('common:seo.home.keywords')}
      />

      <style>{styles}</style>

      <nav className="lp-nav">
        <Link href="/" className="brand">
          <img src="/images/logo_horizontal_transparent.png" alt="Sloty" className="brand-logo" />
        </Link>
        <div className="links desktop-only">
          <Link href="/features">{t('home:nav.features')}</Link>
          <div
            className="solutions"
            onMouseEnter={openSolutions}
            onMouseLeave={scheduleCloseSolutions}
          >
            <button className="solutions-trigger" type="button">
              {t('home:nav.solutions')} <ChevronDown className="chev" />
            </button>
            {solutionsOpen && (
              <div
                className="solutions-panel"
                onMouseEnter={openSolutions}
                onMouseLeave={scheduleCloseSolutions}
              >
                <div className="sol-grid">
                  {SOLUTIONS.map((cat) => (
                    <div key={cat.labelKey} className="sol-col">
                      <div className="sol-h">{t(cat.labelKey)}</div>
                      <ul>
                        {cat.items.map(({ labelKey, href, Icon }) => (
                          <li key={href}>
                            <Link href={localizeHref(href, i18n.language)}>
                              <Icon className="sol-ic" /> {t(labelKey)}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <a href="#pricing">{t('home:nav.pricing')}</a>
          <Link href="/about">{t('home:nav.about')}</Link>
          <div className="lang-wrap">
            <LanguageSelector />
          </div>
          {isAuthenticated ? (
            <Link href={dashboardUrl} className="cta">{t('home:nav.dashboard')}</Link>
          ) : (
            <>
              <Link href="/login">{t('home:nav.login')}</Link>
              <Link href="/register" className="cta">{t('home:nav.startFree')}</Link>
            </>
          )}
        </div>

        <div className="mobile-actions mobile-only">
          <div className="lang-wrap">
            <LanguageSelector />
          </div>
          <button
            className="mobile-toggle"
            onClick={() => setMobileMenuOpen((v) => !v)}
            aria-label={t('home:nav.toggleMenu')}
            type="button"
          >
            {mobileMenuOpen ? <X className="ic" /> : <Menu className="ic" />}
          </button>
        </div>
      </nav>

      {mobileMenuOpen && (
        <div className="mobile-menu mobile-only">
          <Link href="/features" onClick={() => setMobileMenuOpen(false)}>{t('home:nav.features')}</Link>
          <button
            type="button"
            className="m-solutions-trigger"
            onClick={() => setMobileSolutionsOpen((v) => !v)}
          >
            {t('home:nav.solutions')} <ChevronDown className={`chev ${mobileSolutionsOpen ? 'open' : ''}`} />
          </button>
          {mobileSolutionsOpen && (
            <div className="m-solutions">
              {SOLUTIONS.map((cat) => (
                <div key={cat.labelKey}>
                  <div className="sol-h">{t(cat.labelKey)}</div>
                  {cat.items.map(({ labelKey, href }) => (
                    <Link key={href} href={localizeHref(href, i18n.language)} onClick={() => setMobileMenuOpen(false)}>
                      {t(labelKey)}
                    </Link>
                  ))}
                </div>
              ))}
            </div>
          )}
          <a href="#pricing" onClick={() => setMobileMenuOpen(false)}>{t('home:nav.pricing')}</a>
          <Link href="/about" onClick={() => setMobileMenuOpen(false)}>{t('home:nav.about')}</Link>
          {isAuthenticated ? (
            <Link href={dashboardUrl} onClick={() => setMobileMenuOpen(false)} className="cta">{t('home:nav.dashboard')}</Link>
          ) : (
            <>
              <Link href="/login" onClick={() => setMobileMenuOpen(false)}>{t('home:nav.login')}</Link>
              <Link href="/register" onClick={() => setMobileMenuOpen(false)} className="cta">{t('home:nav.startFree')}</Link>
            </>
          )}
        </div>
      )}

      <div>
        <div className="wrap">
          <div className="announce">
            <a href="#session-notes" className="announce-pill">
              <NotebookText className="a-ic" />
              <span>{t('home:announce.text')}</span>
              <span className="a-arrow"><ArrowRight /></span>
            </a>
          </div>

          <section className="hero">
            <div>
              <span className="eyebrow"><span className="pulse"></span> {t('home:hero.eyebrow')}</span>
              <h1 className="hero-h">
                {t('home:hero.titlePrefix')}{' '}
                <span className="rotator-wrap">
                  <span className={`accent rotator ${fading ? 'rotator-out' : 'rotator-in'}`}>
                    {rotatingWords[wordIdx]}
                  </span>
                </span>
                {t('home:hero.titleSuffix')}
              </h1>
              <p className="lead">
                {t('home:hero.lead')}
              </p>
              <div className="cta-row">
                {isAuthenticated ? (
                  <Link href={dashboardUrl} className="btn primary">{t('home:hero.ctaDashboard')}</Link>
                ) : (
                  <Link href="/register" className="btn primary">{t('home:hero.ctaGetPage')}</Link>
                )}
                <span className="tiny">{t('home:hero.setupTime')}</span>
              </div>
            </div>

            <div className="calendar team-cal">
              <div className="cal-head">
                <div>
                  <h3>{t('home:calendarDemo.title')}</h3>
                  <div className="cal-sub">{t('home:calendarDemo.subtitle')}</div>
                </div>
                <div className="cal-nav"><button>‹</button><button>›</button></div>
              </div>
              <div className="staff-row">
                <div className="time-spacer"></div>
                {[1, 2, 3, 4, 5].map((n) => (
                  <div className="staff-col" key={n}>
                    <div className="avatar">S{n}</div>
                    <div className="staff-name">{t('home:calendarDemo.staffPrefix')} {n}</div>
                  </div>
                ))}
              </div>
              <div className="cal-body">
                <div className="time-col">
                  <div className="hour">09:00</div>
                  <div className="hour">10:00</div>
                  <div className="hour">11:00</div>
                  <div className="hour">12:00</div>
                  <div className="hour">13:00</div>
                </div>
                <div className="lane">
                  <div className="event" style={{ top: 0, height: 45 }}><div className="ev-n">Maxime F.</div><div className="ev-s">{t('home:calendarDemo.services.cutAndStyle')}</div></div>
                  <div className="event" style={{ top: 48, height: 57 }}><div className="ev-n">Liam O.</div><div className="ev-s">{t('home:calendarDemo.services.treatment')}</div></div>
                  <div className="event" style={{ top: 120, height: 60 }}><div className="ev-n">Raphaël G.</div><div className="ev-s">{t('home:calendarDemo.services.treatment')}</div></div>
                  <div className="event" style={{ top: 210, height: 45 }}><div className="ev-n">Sarah K.</div><div className="ev-s">{t('home:calendarDemo.services.cut')}</div></div>
                </div>
                <div className="lane">
                  <div className="event" style={{ top: 0, height: 90 }}><div className="ev-n">Théo F.</div><div className="ev-s">{t('home:calendarDemo.services.colorAndHighlights')}</div></div>
                  <div className="event" style={{ top: 93, height: 87 }}><div className="ev-n">Léa P.</div><div className="ev-s">{t('home:calendarDemo.services.colorAndHighlights')}</div></div>
                  <div className="event" style={{ top: 195, height: 45 }}><div className="ev-n">Camille R.</div><div className="ev-s">{t('home:calendarDemo.services.cut')}</div></div>
                </div>
                <div className="lane">
                  <div className="event" style={{ top: 0, height: 45 }}><div className="ev-n">Juliette M.</div><div className="ev-s">{t('home:calendarDemo.services.cut')}</div></div>
                  <div className="event" style={{ top: 48, height: 27 }}><div className="ev-n">Tom R.</div><div className="ev-s">{t('home:calendarDemo.services.beard')}</div></div>
                  <div className="event" style={{ top: 120, height: 90 }}><div className="ev-n">Charlotte D.</div><div className="ev-s">{t('home:calendarDemo.services.color')}</div></div>
                  <div className="event" style={{ top: 240, height: 45 }}><div className="ev-n">Eva H.</div><div className="ev-s">{t('home:calendarDemo.services.treatment')}</div></div>
                </div>
                <div className="lane">
                  <div className="event" style={{ top: 0, height: 60 }}><div className="ev-n">Raphaël G.</div><div className="ev-s">{t('home:calendarDemo.services.cut')}</div></div>
                  <div className="event" style={{ top: 75, height: 45 }}><div className="ev-n">Hugo B.</div><div className="ev-s">{t('home:calendarDemo.services.beard')}</div></div>
                  <div className="event" style={{ top: 135, height: 45 }}><div className="ev-n">Sarah K.</div><div className="ev-s">{t('home:calendarDemo.services.cut')}</div></div>
                  <div className="event new" style={{ top: 195, height: 45 }}><div className="ev-n">Anna S.</div><div className="ev-s">{t('home:calendarDemo.services.cut')}</div></div>
                  <div className="event" style={{ top: 255, height: 45 }}><div className="ev-n">Théo F.</div><div className="ev-s">{t('home:calendarDemo.services.cut')}</div></div>
                </div>
                <div className="lane">
                  <div className="event" style={{ top: 0, height: 30 }}><div className="ev-n">Lucas M.</div><div className="ev-s">{t('home:calendarDemo.services.cut')}</div></div>
                  <div className="event" style={{ top: 48, height: 42 }}><div className="ev-n">Anna S.</div><div className="ev-s">{t('home:calendarDemo.services.cut')}</div></div>
                  <div className="event" style={{ top: 105, height: 60 }}><div className="ev-n">Mila C.</div><div className="ev-s">{t('home:calendarDemo.services.color')}</div></div>
                  <div className="event" style={{ top: 180, height: 45 }}><div className="ev-n">Léa P.</div><div className="ev-s">{t('home:calendarDemo.services.cut')}</div></div>
                  <div className="event" style={{ top: 240, height: 45 }}><div className="ev-n">Inès L.</div><div className="ev-s">{t('home:calendarDemo.services.cut')}</div></div>
                </div>
              </div>

              <div className="book-toast">
                <div className="title"><span className="ico"></span> {t('home:calendarDemo.toast.title')}</div>
                <div className="meta-line">{t('home:calendarDemo.toast.meta')}</div>
              </div>
            </div>
          </section>

          <section className="proof">
            <div className="label">{t('home:proof.label')}</div>
            <div className="row">
              {proofCategories.map((cat, i) => (
                <span key={i}>{cat}</span>
              ))}
            </div>
          </section>

          <section className="stats" id="how">
            <div className="stat">
              <div className="num">24<em>/7</em></div>
              <div className="desc">{t('home:statsSection.alwaysOn')}</div>
            </div>
            <div className="stat">
              <div className="num"><em>−</em>50%</div>
              <div className="desc">{t('home:statsSection.noShows')}</div>
            </div>
            <div className="stat">
              <div className="num">+30<em>%</em></div>
              <div className="desc">{t('home:statsSection.moreBookings')}</div>
            </div>
          </section>

          <section id="features">
            <div className="section-head">
              <h2>{t('home:featuresSection.heading.line1')}<br/><em>{t('home:featuresSection.heading.line2Em')}</em>{t('home:featuresSection.heading.line2Rest')}</h2>
              <div className="right">{t('home:featuresSection.label')}<br/>{t('home:featuresSection.range')}</div>
            </div>
            <div className="features">
              <div className="feat">
                <div className="feat-head">
                  <div className="num">— 01</div>
                  <span className="tag-new">{t('home:featuresSection.cards.sessionNotes.badge')}</span>
                </div>
                <h3>{t('home:featuresSection.cards.sessionNotes.title')}</h3>
                <p>{t('home:featuresSection.cards.sessionNotes.description')}</p>
                <div className="vis">
                  <div className="notecard">
                    <div className="nc-head">
                      <div className="nc-av">MK</div>
                      <div className="nc-name">{t('home:featuresSection.cards.sessionNotes.demo.client')}</div>
                      <span className="chip-enc"><Lock /> {t('home:featuresSection.cards.sessionNotes.demo.encrypted')}</span>
                    </div>
                    <div className="nc-bars">
                      <span style={{ width: '100%' }}></span>
                      <span style={{ width: '84%' }}></span>
                      <span style={{ width: '62%' }}></span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="feat">
                <div className="num">— 02</div>
                <h3>{t('home:featuresSection.cards.groupClasses.title')}</h3>
                <p>{t('home:featuresSection.cards.groupClasses.description')}</p>
                <div className="vis">
                  <div className="classcard">
                    <div className="cc-head">
                      <div>
                        <div className="cc-name">{t('home:featuresSection.cards.groupClasses.demo.className')}</div>
                        <div className="cc-when">{t('home:featuresSection.cards.groupClasses.demo.when')}</div>
                      </div>
                      <div className="cc-spots">{t('home:featuresSection.cards.groupClasses.demo.spots')}</div>
                    </div>
                    <div className="cc-bar"><span style={{ width: '80%' }}></span></div>
                    <span className="chip-enc"><Users /> {t('home:featuresSection.cards.groupClasses.demo.waitlist')}</span>
                  </div>
                </div>
              </div>
              <div className="feat">
                <div className="num">— 03</div>
                <h3>{t('home:featuresSection.cards.reminders.title')}</h3>
                <p>{t('home:featuresSection.cards.reminders.description')}</p>
                <div className="vis">
                  <div className="email">
                    <div className="email-head">
                      <div className="email-avatar">S</div>
                      <div className="email-meta">
                        <div className="email-from">{t('home:featuresSection.cards.reminders.demo.from')}</div>
                        <div className="email-addr">hello@slotyapp.com</div>
                      </div>
                      <div className="email-time">9:02</div>
                    </div>
                    <div className="email-subject">{t('home:featuresSection.cards.reminders.demo.subject')}</div>
                    <div className="email-body">{t('home:featuresSection.cards.reminders.demo.body')}</div>
                  </div>
                </div>
              </div>
              <div className="feat">
                <div className="num">— 04</div>
                <h3>{t('home:featuresSection.cards.brand.title')}</h3>
                <p>{t('home:featuresSection.cards.brand.description')}</p>
                <div className="vis">
                  <div className="pay">
                    <div className="chip"></div>
                    <div className="num2">your-shop.slotyapp.com</div>
                    <div className="ok">{t('home:featuresSection.cards.brand.demo.live')}</div>
                  </div>
                </div>
              </div>
              <div className="feat">
                <div className="num">— 05</div>
                <h3>{t('home:featuresSection.cards.calendar.title')}</h3>
                <p>{t('home:featuresSection.cards.calendar.description')}</p>
                <div className="vis">
                  <div className="rings">
                    <div className="ring r1">G</div>
                    <div className="ring r2">A</div>
                  </div>
                </div>
              </div>
              <div className="feat">
                <div className="num">— 06</div>
                <h3>{t('home:featuresSection.cards.customerManagement.title')}</h3>
                <p>{t('home:featuresSection.cards.customerManagement.description')}</p>
                <div className="vis">
                  <div className="clist">
                    <div className="crow">
                      <div className="cav">AB</div>
                      <div>
                        <div className="cname">{t('home:featuresSection.cards.customerManagement.demo.row1Name')}</div>
                        <div className="cmeta">{t('home:featuresSection.cards.customerManagement.demo.row1Service')}</div>
                      </div>
                      <div className="cvisits">{t('home:featuresSection.cards.customerManagement.demo.row1Visits')}</div>
                    </div>
                    <div className="crow">
                      <div className="cav" style={{ background: 'var(--brand-700)' }}>CD</div>
                      <div>
                        <div className="cname">{t('home:featuresSection.cards.customerManagement.demo.row2Name')}</div>
                        <div className="cmeta">{t('home:featuresSection.cards.customerManagement.demo.row2Service')}</div>
                      </div>
                      <div className="cvisits">{t('home:featuresSection.cards.customerManagement.demo.row2Visits')}</div>
                    </div>
                    <div className="crow">
                      <div className="cav" style={{ background: 'var(--brand-300)', color: 'var(--brand-700)' }}>ME</div>
                      <div>
                        <div className="cname">{t('home:featuresSection.cards.customerManagement.demo.row3Name')}</div>
                        <div className="cmeta">{t('home:featuresSection.cards.customerManagement.demo.row3Service')}</div>
                      </div>
                      <div className="cvisits">{t('home:featuresSection.cards.customerManagement.demo.row3Visits')}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="spotlight" id="session-notes">
            <div className="sp-copy">
              <div className="sp-eyebrow">{t('home:sessionNotes.eyebrow')}</div>
              <h2>{t('home:sessionNotes.title')}</h2>
              <p>{t('home:sessionNotes.subtitle')}</p>
              <Link href={localizeHref('/for-therapists', i18n.language)} className="btn sp-cta">
                {t('home:sessionNotes.cta')} <ArrowRight className="btn-ic" />
              </Link>
            </div>

            <div className="sp-mock">
              <div className="sp-client">
                <div className="sp-av">MK</div>
                <div className="sp-who">
                  <div className="sp-name">{t('home:sessionNotes.demo.client')}</div>
                  <div className="sp-meta">{t('home:sessionNotes.demo.meta')}</div>
                </div>
                <span className="chip-enc"><Lock /> {t('home:sessionNotes.demo.encrypted')}</span>
              </div>
              <div className="sp-entries">
                <div className="sp-entry">
                  <div className="sp-date">{t('home:sessionNotes.demo.entry1')}</div>
                  <span style={{ width: '100%' }}></span>
                  <span style={{ width: '88%' }}></span>
                  <span style={{ width: '54%' }}></span>
                </div>
                <div className="sp-entry">
                  <div className="sp-date">{t('home:sessionNotes.demo.entry2')}</div>
                  <span style={{ width: '92%' }}></span>
                  <span style={{ width: '68%' }}></span>
                </div>
                <div className="sp-entry faded">
                  <div className="sp-date">{t('home:sessionNotes.demo.entry3')}</div>
                  <span style={{ width: '76%' }}></span>
                </div>
              </div>
              <div className="sp-add"><Plus /> {t('home:sessionNotes.demo.addNote')}</div>
            </div>
          </section>

          <section className="router" id="verticals">
            <div className="router-head">
              <div className="sp-eyebrow muted">{t('home:router.eyebrow')}</div>
              <h2>{t('home:router.title')}</h2>
              <p>{t('home:router.subtitle')}</p>
            </div>

            <div className="router-featured">
              <Link href={localizeHref('/for-therapists', i18n.language)} className="rcard rcard-lg">
                <div className="rc-head">
                  <span className="r-ic"><HeartHandshake /></span>
                  <h3>{t('home:router.featured.therapists.title')}</h3>
                  <ArrowRight className="rc-arrow" />
                </div>
                <p>{t('home:router.featured.therapists.description')}</p>
                <div className="rc-mock">
                  <div className="rc-row">
                    <div className="rc-av">MK</div>
                    <div className="rc-label">{t('home:router.featured.therapists.demo.note')}</div>
                    <span className="chip-enc ghost"><Lock /> {t('home:sessionNotes.demo.encrypted')}</span>
                  </div>
                  <div className="nc-bars">
                    <span style={{ width: '100%' }}></span>
                    <span style={{ width: '70%' }}></span>
                  </div>
                </div>
              </Link>

              <Link href={localizeHref('/for-small-gyms', i18n.language)} className="rcard rcard-lg">
                <div className="rc-head">
                  <span className="r-ic"><Users /></span>
                  <h3>{t('home:router.featured.smallGyms.title')}</h3>
                  <ArrowRight className="rc-arrow" />
                </div>
                <p>{t('home:router.featured.smallGyms.description')}</p>
                <div className="rc-mock">
                  <div className="cc-head">
                    <div>
                      <div className="cc-name">{t('home:featuresSection.cards.groupClasses.demo.className')}</div>
                      <div className="cc-when">{t('home:featuresSection.cards.groupClasses.demo.when')}</div>
                    </div>
                    <div className="cc-spots">{t('home:featuresSection.cards.groupClasses.demo.spots')}</div>
                  </div>
                  <div className="cc-bar on-tint"><span style={{ width: '80%' }}></span></div>
                  <span className="chip-enc ghost">{t('home:featuresSection.cards.groupClasses.demo.waitlist')}</span>
                </div>
              </Link>
            </div>

            <div className="router-compact">
              {ROUTER_COMPACT.map(({ slug, href, Icon, labelKey, hookKey }) => (
                <Link key={slug} href={localizeHref(href, i18n.language)} className={`rcard rcard-sm v-${slug}`}>
                  <span className="r-ic"><Icon /></span>
                  <h3>{t(labelKey)}</h3>
                  <span className="rc-hook">{t(hookKey)}</span>
                </Link>
              ))}
            </div>
          </section>

          <section className="quote">
            <q>{t('home:quote.text')}</q>
            <div className="who">{t('home:quote.author')}</div>
          </section>
        </div>

        <section id="pricing" className="pricing-section">
          <div className="pricing-inner">
            <div className="pricing-head">
              <h2>{t('home:pricing.sectionTitle')}</h2>
              <p>{t('home:pricing.sectionSubtitle')}</p>
            </div>
            <PricingTable
              currency={currency.toLowerCase()}
              interval="year"
              isSubscribed={false}
              signupMode
            />
          </div>
        </section>

        <section className="final-cta">
          <div className="final-cta-inner">
            <h2>{t('home:finalCta.title')}</h2>
            <p>{t('home:finalCta.subtitle')}</p>
            <Link href={isAuthenticated ? dashboardUrl : '/register'} className="btn primary">
              {isAuthenticated ? t('home:hero.ctaDashboard') : t('home:nav.startFree')}
            </Link>
          </div>
        </section>
      </div>

      <footer className="lp-footer">
        <div>
          <div className="brand" style={{ marginBottom: 14 }}>
            <img src="/images/logo_horizontal_transparent.png" alt="Sloty" className="brand-logo footer-logo" />
          </div>
          <p className="tagline">{t('home:footer.tagline')}</p>
        </div>
        <div>
          <h5>{t('home:footer.product')}</h5>
          <ul>
            <li><Link href="/features">{t('home:footer.features')}</Link></li>
            <li><a href="#pricing">{t('home:footer.pricing')}</a></li>
            <li><Link href="/about">{t('home:footer.about')}</Link></li>
            <li><Link href="/blog">{t('home:footer.blog')}</Link></li>
          </ul>
        </div>
        <div>
          <h5>{t('home:footer.solutions')}</h5>
          <ul>
            <li><Link href={localizeHref('/for-salons', i18n.language)}>{t('home:footer.solutionsLinks.salonsBarbers')}</Link></li>
            <li><Link href={localizeHref('/for-clinics', i18n.language)}>{t('home:footer.solutionsLinks.clinicsDoctors')}</Link></li>
            <li><Link href={localizeHref('/for-trainers', i18n.language)}>{t('home:footer.solutionsLinks.trainers')}</Link></li>
            <li><Link href={localizeHref('/for-small-gyms', i18n.language)}>{t('home:footer.solutionsLinks.smallGyms')}</Link></li>
            <li><Link href={localizeHref('/for-therapists', i18n.language)}>{t('home:footer.solutionsLinks.therapists')}</Link></li>
            <li><Link href={localizeHref('/for-coaches', i18n.language)}>{t('home:footer.solutionsLinks.coaches')}</Link></li>
          </ul>
        </div>
        <div>
          <h5>{t('home:footer.legal')}</h5>
          <ul>
            <li><Link href="/privacy">{t('home:footer.legalLinks.privacy')}</Link></li>
            <li><Link href="/terms">{t('home:footer.legalLinks.terms')}</Link></li>
            <li><Link href="/cookies">{t('home:footer.legalLinks.cookies')}</Link></li>
            <li><Link href="/impressum">{t('home:footer.legalLinks.impressum')}</Link></li>
          </ul>
        </div>
      </footer>
    </div>
  );
}

const styles = `
.lp-a-root{
  --background: 210 20% 98%;
  --foreground: 215 25% 27%;
  --muted: 220 14% 96%;
  --muted-foreground: 215 16% 35%;
  --border: 220 13% 91%;
  --action-primary: 217 91% 53%;
  --action-primary-hover: 224 76% 42%;
  --action-primary-light: 217 91% 95%;
  --brand-100:#DBEAFE;
  --brand-300:#93C5FD;
  --brand-500:#2563EB;
  --brand-700:#1E40AF;
  --brand-950:#0F172A;
  --status-available:#10B981;
  --status-booked:#3B82F6;
  --radius:0.9375rem;
  background:hsl(var(--background));
  color:hsl(var(--foreground));
  font-family:'Inter Variable',Inter,system-ui,-apple-system,'Segoe UI',sans-serif;
  -webkit-font-smoothing:antialiased;
  line-height:1.75;
}
.lp-a-root *{box-sizing:border-box;}
.lp-a-root h1,.lp-a-root h2,.lp-a-root h3,.lp-a-root h4,.lp-a-root h5,.lp-a-root h6{font-family:'Lexend Variable',Lexend,system-ui,-apple-system,sans-serif;font-weight:600;letter-spacing:-0.01em;line-height:1.1;margin:0;}
.lp-a-root .wrap a,.lp-a-root .final-cta a,.lp-a-root .lp-nav a,.lp-a-root .lp-footer a,.lp-a-root .mobile-menu a{color:inherit;text-decoration:none;cursor:pointer;}
.lp-a-root .lp-nav{display:flex;justify-content:space-between;align-items:center;padding:22px 32px;max-width:1320px;margin:0 auto;position:relative;}
.lp-a-root .brand{display:inline-flex;align-items:center;color:hsl(var(--foreground));}
.lp-a-root .brand-logo{height:40px;width:auto;display:block;}
.lp-a-root .footer-logo{height:32px;}
.lp-a-root .lp-nav .links{display:flex;gap:28px;align-items:center;font-size:14px;color:hsl(var(--muted-foreground));}
.lp-a-root .lp-nav .links a:not(.cta):hover,.lp-a-root .lp-nav .solutions-trigger:hover{color:hsl(var(--foreground));}
.lp-a-root .lp-nav .cta{background:hsl(var(--action-primary));color:#fff;padding:10px 18px;border-radius:9999px;font-size:14px;font-weight:500;min-height:44px;display:inline-flex;align-items:center;}
.lp-a-root .lp-nav .cta:hover{background:hsl(var(--action-primary-hover));color:#fff;}
.lp-a-root .solutions{position:relative;}
.lp-a-root .solutions-trigger{background:none;border:0;padding:0;font:inherit;color:inherit;cursor:pointer;display:inline-flex;align-items:center;gap:4px;font-size:14px;}
.lp-a-root .solutions-trigger .chev{width:14px;height:14px;}
.lp-a-root .solutions-panel{position:absolute;top:100%;left:50%;transform:translateX(-50%);padding-top:10px;min-width:560px;z-index:60;}
.lp-a-root .solutions-panel > .sol-grid{background:#fff;border:1px solid hsl(var(--border));border-radius:14px;padding:20px;display:grid;grid-template-columns:repeat(3,1fr);gap:24px;box-shadow:0 14px 40px rgba(15,23,42,0.10);}
.lp-a-root .solutions-panel .sol-h{font-size:11px;font-weight:600;letter-spacing:0.06em;text-transform:uppercase;color:hsl(var(--muted-foreground));margin-bottom:10px;}
.lp-a-root .solutions-panel ul{list-style:none;padding:0;margin:0;display:flex;flex-direction:column;gap:4px;}
.lp-a-root .solutions-panel li a{display:flex;align-items:center;gap:8px;padding:6px 8px;border-radius:8px;font-size:14px;color:hsl(var(--foreground));}
.lp-a-root .solutions-panel li a:hover{background:hsl(var(--action-primary-light));color:var(--brand-700);}
.lp-a-root .sol-ic{width:16px;height:16px;color:var(--brand-500);}
.lp-a-root .mobile-toggle{background:none;border:0;padding:10px;cursor:pointer;color:hsl(var(--foreground));min-width:44px;min-height:44px;display:inline-flex;align-items:center;justify-content:center;}
.lp-a-root .mobile-toggle .ic{width:24px;height:24px;}
.lp-a-root .mobile-actions{align-items:center;gap:8px;}
.lp-a-root .mobile-actions .lang-wrap [data-radix-select-trigger],.lp-a-root .mobile-actions .lang-wrap button[role="combobox"]{height:36px;min-height:0;padding:6px 8px;border-radius:9999px;background:transparent;border:1px solid hsl(var(--border));font-size:13px;color:hsl(var(--muted-foreground));width:auto;}
.lp-a-root .lp-nav .mobile-only,.lp-a-root .mobile-only{display:none;}
.lp-a-root .lp-nav .desktop-only,.lp-a-root .desktop-only{display:flex;}
.lp-a-root .mobile-menu{display:none;flex-direction:column;gap:4px;padding:12px 24px 20px;border-bottom:1px solid hsl(var(--border));background:hsl(var(--background));}
.lp-a-root .mobile-menu a,.lp-a-root .mobile-menu .m-solutions-trigger{padding:12px 8px;font-size:15px;color:hsl(var(--foreground));border-radius:8px;background:none;border:0;text-align:left;font-family:inherit;display:flex;align-items:center;justify-content:space-between;cursor:pointer;}
.lp-a-root .mobile-menu .cta{background:hsl(var(--action-primary));color:#fff;justify-content:center;margin-top:6px;}
.lp-a-root .mobile-menu .m-solutions-trigger .chev{width:16px;height:16px;transition:transform .2s;}
.lp-a-root .mobile-menu .m-solutions-trigger .chev.open{transform:rotate(180deg);}
.lp-a-root .m-solutions{display:flex;flex-direction:column;gap:14px;padding:6px 0 6px 12px;border-left:2px solid hsl(var(--border));margin-left:8px;}
.lp-a-root .m-solutions .sol-h{font-size:11px;font-weight:600;letter-spacing:0.06em;text-transform:uppercase;color:hsl(var(--muted-foreground));margin-bottom:4px;}
.lp-a-root .m-solutions a{padding:8px;font-size:14px;}
.lp-a-root .lp-footer{border-top:1px solid hsl(var(--border));padding:48px 32px;display:grid;grid-template-columns:1fr 1fr 1fr 1fr;gap:32px;max-width:1320px;margin:0 auto;}
.lp-a-root .lp-footer h5{font-size:11px;font-weight:600;letter-spacing:0.08em;text-transform:uppercase;color:hsl(var(--muted-foreground));margin:0 0 14px;}
.lp-a-root .lp-footer ul{list-style:none;padding:0;margin:0;display:flex;flex-direction:column;gap:8px;font-size:14px;color:hsl(var(--muted-foreground));}
.lp-a-root .lp-footer ul a:hover{color:hsl(var(--foreground));}
.lp-a-root .lp-footer .tagline{font-size:13px;color:hsl(var(--muted-foreground));max-width:240px;margin:0;}
.lp-a-root .lp-nav .lang-wrap{display:inline-flex;align-items:center;}
.lp-a-root .lp-nav .lang-wrap [data-radix-select-trigger],.lp-a-root .lp-nav .lang-wrap button[role="combobox"]{height:36px;min-height:0;padding:6px 10px;border-radius:9999px;background:transparent;border:1px solid hsl(var(--border));font-size:13px;color:hsl(var(--muted-foreground));}
.lp-a-root .mobile-menu .m-lang{padding:8px 0;}
.lp-a-root .wrap{max-width:1320px;margin:0 auto;padding:0 32px;}
.lp-a-root .hero{display:grid;grid-template-columns:1.05fr 1fr;gap:48px;align-items:center;padding:48px 0 80px;}
.lp-a-root .eyebrow{display:inline-flex;align-items:center;gap:8px;padding:6px 14px;background:hsl(var(--action-primary-light));color:var(--brand-700);border-radius:9999px;font-size:12px;font-weight:500;letter-spacing:0.02em;}
.lp-a-root .eyebrow .pulse{width:7px;height:7px;border-radius:50%;background:var(--status-available);box-shadow:0 0 0 0 rgba(16,185,129,0.6);animation:lpaPulse 1.6s infinite;}
@keyframes lpaPulse{0%{box-shadow:0 0 0 0 rgba(16,185,129,0.55);}70%{box-shadow:0 0 0 10px rgba(16,185,129,0);}100%{box-shadow:0 0 0 0 rgba(16,185,129,0);}}
.lp-a-root h1.hero-h{font-size:60px;line-height:1.1;letter-spacing:-0.02em;margin:18px 0 0;font-weight:700;}
.lp-a-root h1.hero-h .accent{color:var(--brand-500);display:inline-block;}
.lp-a-root .rotator-wrap{display:inline-block;white-space:nowrap;}
.lp-a-root .rotator{display:inline-block;white-space:nowrap;transition:opacity .45s cubic-bezier(.4,0,.2,1), transform .45s cubic-bezier(.4,0,.2,1), filter .45s ease;will-change:opacity,transform;}
.lp-a-root .rotator-in{opacity:1;transform:translateY(0);filter:blur(0);}
.lp-a-root .rotator-out{opacity:0;transform:translateY(-10px);filter:blur(2px);}
.lp-a-root .lead{margin-top:22px;font-size:20px;line-height:1.6;color:hsl(var(--muted-foreground));max-width:520px;}
.lp-a-root .cta-row{margin-top:32px;display:flex;gap:12px;align-items:center;flex-wrap:wrap;}
.lp-a-root .btn{display:inline-flex;align-items:center;gap:8px;padding:0 24px;min-height:56px;border-radius:var(--radius);font-size:17px;font-weight:500;border:1px solid hsl(var(--border));background:#fff;cursor:pointer;font-family:inherit;color:hsl(var(--foreground));transition:transform .15s ease, background .15s ease, border-color .15s ease;}
.lp-a-root .btn:hover{transform:translateY(-1px);}
.lp-a-root .btn.primary{background:hsl(var(--action-primary));color:#fff;border-color:hsl(var(--action-primary));}
.lp-a-root .btn.primary:hover{background:hsl(var(--action-primary-hover));border-color:hsl(var(--action-primary-hover));color:#fff;}
.lp-a-root .tiny{font-size:12px;color:hsl(var(--muted-foreground));margin-left:8px;}
.lp-a-root .calendar{background:#fff;border-radius:18px;padding:20px;box-shadow:0 30px 80px rgba(15,23,42,0.10), 0 4px 12px rgba(15,23,42,0.04);position:relative;border:1px solid hsl(var(--border));}
.lp-a-root .cal-head{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:14px;gap:12px;}
.lp-a-root .cal-head h3{font-size:17px;font-weight:600;line-height:1.2;}
.lp-a-root .cal-sub{font-size:11px;color:hsl(var(--muted-foreground));margin-top:3px;}
.lp-a-root .cal-nav{display:flex;gap:6px;flex-shrink:0;}
.lp-a-root .cal-nav button{width:30px;height:30px;border:1px solid hsl(var(--border));border-radius:8px;background:#fff;font-size:14px;cursor:pointer;color:hsl(var(--foreground));}
.lp-a-root .cal-nav button:hover{background:hsl(var(--muted));}
.lp-a-root .team-cal .staff-row{display:grid;grid-template-columns:38px repeat(5,1fr);gap:4px;padding-bottom:10px;border-bottom:1px solid hsl(var(--border));}
.lp-a-root .team-cal .time-spacer{}
.lp-a-root .team-cal .staff-col{display:flex;flex-direction:column;align-items:center;gap:4px;}
.lp-a-root .team-cal .avatar{width:24px;height:24px;border-radius:50%;background:hsl(var(--action-primary-light));color:var(--brand-700);display:flex;align-items:center;justify-content:center;font-size:10px;font-weight:600;font-family:'Lexend Variable',Lexend,sans-serif;}
.lp-a-root .team-cal .staff-name{font-size:10.5px;font-weight:500;color:hsl(var(--foreground));}
.lp-a-root .team-cal .cal-body{display:grid;grid-template-columns:38px repeat(5,1fr);gap:4px;padding-top:8px;}
.lp-a-root .team-cal .time-col{display:flex;flex-direction:column;}
.lp-a-root .team-cal .time-col .hour{height:60px;font-size:10px;color:hsl(var(--muted-foreground));font-variant-numeric:tabular-nums;line-height:1;padding-top:0;transform:translateY(-5px);}
.lp-a-root .team-cal .lane{position:relative;height:300px;background-image:linear-gradient(to bottom,hsl(var(--border)) 1px,transparent 1px);background-size:100% 60px;background-position:0 0;border-radius:4px;}
.lp-a-root .team-cal .event{position:absolute;left:2px;right:2px;background:var(--brand-100);border-left:2px solid var(--brand-500);border-radius:5px;padding:4px 6px;font-size:9.5px;line-height:1.2;color:var(--brand-700);overflow:hidden;display:flex;flex-direction:column;justify-content:center;}
.lp-a-root .team-cal .event .ev-n{font-weight:600;font-size:10px;color:var(--brand-700);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.lp-a-root .team-cal .event .ev-s{font-size:9px;color:var(--brand-500);opacity:0.85;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;margin-top:1px;}
.lp-a-root .team-cal .event.new{background:var(--brand-500);border-left-color:var(--brand-700);color:#fff;animation:lpaNewpulse 2s ease infinite;}
.lp-a-root .team-cal .event.new .ev-n,.lp-a-root .team-cal .event.new .ev-s{color:#fff;opacity:1;}
@keyframes lpaNewpulse{0%,100%{box-shadow:0 0 0 0 rgba(37,99,235,0.5);}50%{box-shadow:0 0 0 6px rgba(37,99,235,0);}}
.lp-a-root .book-toast{position:absolute;right:-30px;top:30%;background:#fff;border:1px solid hsl(var(--border));border-radius:14px;padding:12px 14px;box-shadow:0 14px 40px rgba(15,23,42,0.12);min-width:210px;animation:lpaFloat 5s ease-in-out infinite;}
@keyframes lpaFloat{0%,100%{transform:translateY(0);}50%{transform:translateY(-6px);}}
.lp-a-root .book-toast .title{font-size:13px;font-weight:600;display:flex;align-items:center;gap:8px;margin-bottom:4px;}
.lp-a-root .book-toast .title .ico{width:8px;height:8px;border-radius:50%;background:var(--status-available);}
.lp-a-root .book-toast .meta-line{font-size:11px;color:hsl(var(--muted-foreground));font-weight:500;letter-spacing:0.04em;}
.lp-a-root .proof{padding:36px 0;border-top:1px solid hsl(var(--border));border-bottom:1px solid hsl(var(--border));display:grid;grid-template-columns:auto 1fr;gap:32px;align-items:center;}
.lp-a-root .proof .label{font-size:12px;font-weight:600;letter-spacing:0.06em;text-transform:uppercase;color:hsl(var(--muted-foreground));max-width:220px;}
.lp-a-root .proof .row{display:flex;flex-wrap:wrap;gap:24px;align-items:center;font-family:'Lexend Variable',Lexend,sans-serif;font-size:16px;color:hsl(var(--foreground));opacity:0.75;font-weight:500;}
.lp-a-root .stats{padding:88px 0;display:grid;grid-template-columns:repeat(3,1fr);gap:0;}
.lp-a-root .stat{padding:0 24px;border-left:1px solid hsl(var(--border));}
.lp-a-root .stat:first-child{border-left:none;padding-left:0;}
.lp-a-root .stat .num{font-family:'Lexend Variable',Lexend,sans-serif;font-size:64px;line-height:1;letter-spacing:-0.03em;margin-bottom:14px;font-weight:600;color:hsl(var(--foreground));}
.lp-a-root .stat .num em{font-style:normal;color:var(--brand-500);}
.lp-a-root .stat .desc{font-size:14px;color:hsl(var(--muted-foreground));line-height:1.5;max-width:200px;}
.lp-a-root .section-head{display:flex;justify-content:space-between;align-items:end;padding:60px 0 36px;border-top:1px solid hsl(var(--border));gap:32px;}
.lp-a-root .section-head h2{font-size:48px;line-height:1.05;letter-spacing:-0.02em;max-width:700px;font-weight:700;}
.lp-a-root .section-head h2 em{font-style:normal;color:var(--brand-500);}
.lp-a-root .section-head .right{font-size:12px;font-weight:600;letter-spacing:0.06em;text-transform:uppercase;color:hsl(var(--muted-foreground));text-align:right;line-height:1.7;}
.lp-a-root .features{display:grid;grid-template-columns:repeat(2,1fr);gap:24px;padding-bottom:80px;}
.lp-a-root .feat{background:#fff;border-radius:16px;padding:28px;border:1px solid hsl(var(--border));display:flex;flex-direction:column;gap:14px;min-height:300px;position:relative;overflow:hidden;}
.lp-a-root .feat .num{font-size:11px;font-weight:600;letter-spacing:0.06em;color:var(--brand-500);text-transform:uppercase;}
.lp-a-root .feat h3{font-size:24px;letter-spacing:-0.01em;line-height:1.2;font-weight:600;}
.lp-a-root .feat p{font-size:15px;color:hsl(var(--muted-foreground));line-height:1.6;margin:0;}
.lp-a-root .feat .vis{margin-top:auto;display:flex;align-items:center;justify-content:center;}
.lp-a-root .feat .clist{width:100%;max-width:340px;background:#fff;border:1px solid hsl(var(--border));border-radius:12px;padding:8px;display:flex;flex-direction:column;gap:4px;box-shadow:0 4px 14px rgba(15,23,42,0.06);}
.lp-a-root .feat .crow{display:grid;grid-template-columns:32px 1fr auto;gap:10px;align-items:center;padding:8px 10px;border-radius:8px;}
.lp-a-root .feat .crow:hover{background:hsl(var(--action-primary-light));}
.lp-a-root .feat .cav{width:32px;height:32px;border-radius:50%;background:var(--brand-500);color:#fff;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:600;}
.lp-a-root .feat .cname{font-size:13px;font-weight:500;color:hsl(var(--foreground));line-height:1.2;}
.lp-a-root .feat .cmeta{font-size:11px;color:hsl(var(--muted-foreground));line-height:1.2;margin-top:2px;}
.lp-a-root .feat .cvisits{font-size:11px;color:hsl(var(--muted-foreground));white-space:nowrap;}
.lp-a-root .email{background:#fff;border:1px solid hsl(var(--border));border-radius:12px;width:100%;font-size:11px;box-shadow:0 4px 14px rgba(15,23,42,0.05);}
.lp-a-root .email-head{display:flex;align-items:center;gap:10px;padding:10px 12px;border-bottom:1px solid hsl(var(--border));}
.lp-a-root .email-avatar{width:28px;height:28px;border-radius:50%;background:linear-gradient(135deg,var(--brand-500),var(--brand-300));color:#fff;display:flex;align-items:center;justify-content:center;font-weight:600;font-size:13px;font-family:'Lexend Variable',Lexend,sans-serif;}
.lp-a-root .email-meta{flex:1;line-height:1.3;}
.lp-a-root .email-from{font-weight:600;color:hsl(var(--foreground));font-size:12px;}
.lp-a-root .email-addr{color:hsl(var(--muted-foreground));font-size:10.5px;}
.lp-a-root .email-time{color:hsl(var(--muted-foreground));font-size:10.5px;}
.lp-a-root .email-subject{padding:8px 12px 4px;font-weight:600;color:hsl(var(--foreground));font-size:12px;}
.lp-a-root .email-body{padding:0 12px 12px;color:hsl(var(--muted-foreground));line-height:1.5;}
.lp-a-root .pay{display:flex;align-items:center;gap:10px;background:hsl(var(--muted));border-radius:12px;padding:14px;width:100%;}
.lp-a-root .pay .chip{width:36px;height:24px;background:linear-gradient(135deg,var(--brand-500),var(--brand-300));border-radius:4px;}
.lp-a-root .pay .num2{font-size:13px;letter-spacing:0.1em;flex:1;font-variant-numeric:tabular-nums;font-weight:500;}
.lp-a-root .pay .ok{font-size:11px;color:var(--status-available);font-weight:600;}
.lp-a-root .rings{display:flex;align-items:center;height:100px;}
.lp-a-root .ring{width:54px;height:54px;border-radius:50%;border:2px solid #fff;display:flex;align-items:center;justify-content:center;font-family:'Lexend Variable',Lexend,sans-serif;font-size:18px;font-weight:600;background:#fff;box-shadow:0 4px 12px rgba(15,23,42,0.08);}
.lp-a-root .ring+.ring{margin-left:-12px;}
.lp-a-root .ring.r1{background:var(--brand-500);color:#fff;}
.lp-a-root .ring.r2{background:var(--brand-100);color:var(--brand-700);}
.lp-a-root .quote{padding:96px 0;text-align:center;}
.lp-a-root .quote q{font-family:'Lexend Variable',Lexend,sans-serif;font-weight:300;font-size:42px;line-height:1.25;letter-spacing:-0.015em;quotes:"\\201C" "\\201D";max-width:900px;display:inline-block;color:hsl(var(--foreground));}
.lp-a-root .quote .who{margin-top:24px;font-size:13px;font-weight:500;letter-spacing:0.04em;color:hsl(var(--muted-foreground));}
.lp-a-root .pricing-section{background:hsl(var(--muted));padding:80px 0;border-top:1px solid hsl(var(--border));}
.lp-a-root .pricing-inner{max-width:1320px;margin:0 auto;padding:0 32px;}
.lp-a-root .pricing-head{text-align:center;margin-bottom:48px;}
.lp-a-root .pricing-head h2{font-family:'Lexend Variable',Lexend,sans-serif;font-size:48px;font-weight:700;letter-spacing:-0.02em;line-height:1.05;color:hsl(var(--foreground));}
.lp-a-root .pricing-head p{margin-top:16px;font-size:18px;color:hsl(var(--muted-foreground));}
.lp-a-root .pricing-section .text-gray-900,.lp-a-root .pricing-section [class*="CardTitle"]{color:hsl(var(--foreground));}
.lp-a-root .pricing-section h3,.lp-a-root .pricing-section h4{color:hsl(var(--foreground));font-family:'Lexend Variable',Lexend,system-ui,-apple-system,sans-serif;}
.lp-a-root .final-cta{padding:96px 32px;text-align:center;background:#fff;border-top:1px solid hsl(var(--border));}
.lp-a-root .final-cta-inner{max-width:720px;margin:0 auto;}
.lp-a-root .final-cta h2{font-family:'Lexend Variable',Lexend,sans-serif;font-size:42px;font-weight:700;letter-spacing:-0.02em;line-height:1.1;color:hsl(var(--foreground));}
.lp-a-root .final-cta p{margin:18px auto 32px;font-size:18px;color:hsl(var(--muted-foreground));max-width:520px;}
.lp-a-root .announce{display:flex;justify-content:center;padding:24px 0 0;}
.lp-a-root .announce + .hero{padding-top:32px;}
.lp-a-root .announce-pill{display:inline-flex;align-items:center;gap:10px;min-height:38px;padding:6px 8px 6px 14px;border:1px solid var(--brand-100);border-radius:9999px;background:hsl(var(--action-primary-light));color:var(--brand-700);font-size:13.5px;font-weight:500;line-height:1.35;transition:background .15s ease,border-color .15s ease;}
.lp-a-root .announce-pill:hover{background:var(--brand-100);border-color:var(--brand-300);color:var(--brand-700);}
.lp-a-root .announce-pill .a-ic{width:16px;height:16px;flex-shrink:0;}
.lp-a-root .announce-pill .a-arrow{width:24px;height:24px;border-radius:9999px;background:var(--brand-100);display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;transition:background .15s ease;}
.lp-a-root .announce-pill .a-arrow svg{width:14px;height:14px;}
.lp-a-root .announce-pill:hover .a-arrow{background:#fff;}
.lp-a-root .feat-head{display:flex;align-items:center;gap:10px;}
.lp-a-root .tag-new{font-size:11px;font-weight:600;letter-spacing:0.04em;text-transform:uppercase;color:var(--brand-700);background:var(--brand-100);border-radius:6px;padding:3px 8px;}
.lp-a-root .chip-enc{display:inline-flex;align-items:center;gap:5px;padding:4px 10px;border-radius:9999px;background:hsl(var(--action-primary-light));color:var(--brand-700);font-size:11px;font-weight:600;white-space:nowrap;}
.lp-a-root .chip-enc svg{width:12px;height:12px;flex-shrink:0;}
.lp-a-root .chip-enc.ghost{background:#fff;border:1px solid hsl(var(--border));}
.lp-a-root .notecard,.lp-a-root .classcard{width:100%;background:#fff;border:1px solid hsl(var(--border));border-radius:12px;padding:14px;box-shadow:0 4px 14px rgba(15,23,42,0.05);}
.lp-a-root .nc-head{display:flex;align-items:center;gap:10px;margin-bottom:12px;}
.lp-a-root .nc-av{width:28px;height:28px;border-radius:50%;background:var(--brand-100);color:var(--brand-700);display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:600;font-family:'Lexend Variable',Lexend,sans-serif;}
.lp-a-root .nc-name{flex:1;font-size:13px;font-weight:600;color:hsl(var(--foreground));}
.lp-a-root .nc-bars{display:flex;flex-direction:column;gap:8px;}
.lp-a-root .nc-bars span{display:block;height:7px;border-radius:4px;background:hsl(var(--border));}
.lp-a-root .cc-head{display:flex;align-items:flex-start;justify-content:space-between;gap:10px;margin-bottom:12px;}
.lp-a-root .cc-name{font-size:13px;font-weight:600;color:hsl(var(--foreground));line-height:1.2;}
.lp-a-root .cc-when{font-size:11px;color:hsl(var(--muted-foreground));margin-top:2px;}
.lp-a-root .cc-spots{font-size:13px;font-weight:600;color:hsl(var(--foreground));white-space:nowrap;font-variant-numeric:tabular-nums;}
.lp-a-root .cc-bar{height:6px;border-radius:9999px;background:hsl(var(--muted));overflow:hidden;margin-bottom:12px;}
.lp-a-root .cc-bar.on-tint{background:#fff;}
.lp-a-root .cc-bar span{display:block;height:100%;border-radius:9999px;background:var(--brand-500);}
.lp-a-root .spotlight{display:grid;grid-template-columns:1.05fr 1fr;gap:64px;align-items:center;background:#fff;border:1px solid hsl(var(--border));border-radius:18px;padding:48px;margin:0 0 80px;}
.lp-a-root .sp-copy{display:flex;flex-direction:column;gap:18px;}
.lp-a-root .sp-eyebrow{font-size:11px;font-weight:600;letter-spacing:0.08em;text-transform:uppercase;color:var(--brand-700);}
.lp-a-root .sp-eyebrow.muted{color:hsl(var(--muted-foreground));}
.lp-a-root .spotlight h2{font-size:48px;line-height:1.05;letter-spacing:-0.025em;font-weight:700;max-width:540px;}
.lp-a-root .spotlight p{font-size:17px;line-height:1.6;color:hsl(var(--muted-foreground));max-width:500px;margin:0;}
.lp-a-root .sp-cta{align-self:flex-start;min-height:48px;font-size:15px;border-radius:10px;color:var(--brand-700);}
.lp-a-root .sp-cta:hover{border-color:var(--brand-300);color:var(--brand-700);}
.lp-a-root .btn-ic{width:16px;height:16px;}
.lp-a-root .sp-mock{background:#fff;border:1px solid hsl(var(--border));border-radius:14px;box-shadow:0 1px 3px rgba(0,0,0,.08),0 4px 16px rgba(0,0,0,.06);padding:20px;display:flex;flex-direction:column;gap:14px;}
.lp-a-root .sp-client{display:flex;align-items:center;gap:12px;}
.lp-a-root .sp-av{width:40px;height:40px;border-radius:50%;background:var(--brand-100);color:var(--brand-700);display:flex;align-items:center;justify-content:center;font-family:'Lexend Variable',Lexend,sans-serif;font-size:14px;font-weight:600;}
.lp-a-root .sp-who{flex:1;}
.lp-a-root .sp-name{font-family:'Lexend Variable',Lexend,sans-serif;font-size:16px;font-weight:600;color:hsl(var(--foreground));line-height:1.2;}
.lp-a-root .sp-meta{font-size:12.5px;color:hsl(var(--muted-foreground));margin-top:2px;}
.lp-a-root .sp-entries{background:hsl(var(--muted));border-radius:12px;padding:14px;display:flex;flex-direction:column;gap:12px;}
.lp-a-root .sp-entry{background:#fff;border:1px solid hsl(var(--border));border-radius:10px;padding:12px 14px;display:flex;flex-direction:column;gap:8px;}
.lp-a-root .sp-entry.faded{opacity:.5;}
.lp-a-root .sp-entry > span{display:block;height:7px;border-radius:4px;background:hsl(var(--border));}
.lp-a-root .sp-date{font-size:11px;font-weight:600;letter-spacing:0.08em;text-transform:uppercase;color:hsl(var(--muted-foreground));}
.lp-a-root .sp-add{display:flex;align-items:center;justify-content:center;gap:8px;min-height:44px;border:1px dashed hsl(var(--border));border-radius:10px;color:hsl(var(--muted-foreground));font-size:14px;font-weight:500;}
.lp-a-root .sp-add svg{width:16px;height:16px;}
.lp-a-root .router{padding:0 0 96px;display:flex;flex-direction:column;gap:32px;}
.lp-a-root .router-head{display:flex;flex-direction:column;gap:14px;}
.lp-a-root .router-head h2{font-size:40px;line-height:1.1;letter-spacing:-0.025em;font-weight:700;}
.lp-a-root .router-head p{font-size:17px;line-height:1.6;color:hsl(var(--muted-foreground));max-width:620px;margin:0;}
.lp-a-root .router-featured{display:grid;grid-template-columns:repeat(2,1fr);gap:24px;}
.lp-a-root .router-compact{display:grid;grid-template-columns:repeat(5,1fr);gap:16px;}
.lp-a-root .rcard{background:#fff;border:1px solid hsl(var(--border));border-radius:14px;display:flex;flex-direction:column;color:hsl(var(--foreground));transition:border-color .15s ease,transform .15s ease;}
.lp-a-root .rcard:hover{border-color:var(--brand-300);transform:translateY(-2px);}
.lp-a-root .rcard-lg{padding:24px;gap:14px;}
.lp-a-root .rcard-sm{padding:18px;gap:10px;}
.lp-a-root .rc-head{display:flex;align-items:center;gap:12px;}
.lp-a-root .rc-head h3{flex:1;font-size:22px;font-weight:600;letter-spacing:-0.02em;}
.lp-a-root .rc-arrow{width:16px;height:16px;color:var(--brand-500);flex-shrink:0;}
.lp-a-root .rcard-lg p{font-size:15px;line-height:1.6;color:hsl(var(--muted-foreground));margin:0;}
.lp-a-root .r-ic{width:36px;height:36px;border-radius:10px;background:hsl(var(--action-primary-light));color:var(--brand-500);display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;}
.lp-a-root .r-ic svg{width:16px;height:16px;}
.lp-a-root .rcard-sm .r-ic{width:28px;height:28px;border-radius:8px;}
.lp-a-root .rcard-sm h3{font-size:15px;font-weight:600;}
.lp-a-root .rc-hook{font-size:12.5px;line-height:1.5;color:hsl(var(--muted-foreground));}
.lp-a-root .rc-mock{margin-top:auto;background:hsl(var(--muted));border-radius:12px;padding:14px;display:flex;flex-direction:column;gap:10px;}
.lp-a-root .rc-row{display:flex;align-items:center;gap:10px;}
.lp-a-root .rc-av{width:26px;height:26px;border-radius:50%;background:var(--brand-100);color:var(--brand-700);display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:600;}
.lp-a-root .rc-label{flex:1;font-size:12.5px;font-weight:600;color:hsl(var(--foreground));}
.lp-a-root .rc-mock .cc-head,.lp-a-root .rc-mock .cc-bar{margin-bottom:0;}
.lp-a-root .rc-mock .chip-enc{align-self:flex-start;}
.lp-a-root .v-salons .r-ic{background:#fdf2f8;color:#9d174d;}
.lp-a-root .v-barbers .r-ic{background:#fffbeb;color:#92400e;}
.lp-a-root .v-clinics .r-ic{background:#f0f9ff;color:#075985;}
.lp-a-root .v-trainers .r-ic{background:#fff7ed;color:#9a3412;}
.lp-a-root .v-coaches .r-ic{background:#f5f3ff;color:#5b21b6;}
@media (max-width:980px){
  .lp-a-root .spotlight{grid-template-columns:1fr;gap:32px;padding:32px;}
  .lp-a-root .spotlight h2{font-size:34px;}
  .lp-a-root .router-featured{grid-template-columns:1fr;}
  .lp-a-root .router-compact{grid-template-columns:repeat(2,1fr);}
  .lp-a-root .router-head h2{font-size:32px;}
  .lp-a-root .hero{grid-template-columns:1fr;}
  .lp-a-root h1.hero-h{font-size:42px;}
  .lp-a-root .stats{grid-template-columns:repeat(2,1fr);gap:24px;}
  .lp-a-root .stat{border-left:none;padding:0;}
  .lp-a-root .features{grid-template-columns:1fr;}
  .lp-a-root .section-head{flex-direction:column;align-items:flex-start;}
  .lp-a-root .section-head h2{font-size:34px;}
  .lp-a-root .quote q{font-size:26px;}
  .lp-a-root .proof{grid-template-columns:1fr;}
  .lp-a-root .book-toast{right:0;}
  .lp-a-root .pricing-head h2,.lp-a-root .final-cta h2{font-size:32px;}
  .lp-a-root .lp-nav .desktop-only,.lp-a-root .desktop-only{display:none;}
  .lp-a-root .lp-nav .mobile-only,.lp-a-root .mobile-only{display:inline-flex;}
  .lp-a-root .mobile-menu{display:flex;}
  .lp-a-root .lp-footer{grid-template-columns:1fr 1fr;}
  .lp-a-root .lp-footer .word{font-size:60px;}
}
@media (max-width:768px){
  .lp-a-root .announce{padding-top:16px;}
  .lp-a-root .announce + .hero{padding-top:20px;}
  .lp-a-root .announce-pill{font-size:13px;border-radius:18px;padding:10px 12px 10px 14px;}
  .lp-a-root .spotlight{padding:24px;margin-bottom:48px;gap:24px;}
  .lp-a-root .spotlight h2{font-size:28px;}
  .lp-a-root .spotlight p{font-size:15px;}
  .lp-a-root .sp-cta{width:100%;justify-content:center;min-height:52px;}
  .lp-a-root .sp-mock{padding:16px;}
  .lp-a-root .router{padding-bottom:56px;gap:24px;}
  .lp-a-root .router-head h2{font-size:28px;}
  .lp-a-root .router-head p{font-size:15px;}
  .lp-a-root .router-compact{gap:12px;}
  .lp-a-root .rc-head h3{font-size:19px;}
  .lp-a-root .lp-nav{padding:14px 20px;}
  .lp-a-root .wrap{padding:0 20px;}
  .lp-a-root .hero{padding:24px 0 48px;gap:32px;}
  .lp-a-root h1.hero-h{font-size:34px;line-height:1.15;margin-top:14px;}
  .lp-a-root .rotator-wrap,.lp-a-root .rotator{white-space:normal;}
  .lp-a-root .lead{font-size:17px;margin-top:18px;max-width:none;}
  .lp-a-root .cta-row{margin-top:24px;gap:10px;}
  .lp-a-root .cta-row .tiny{margin-left:0;}
  .lp-a-root .btn{font-size:16px;padding:0 20px;min-height:52px;}
  .lp-a-root .btn.primary{width:100%;justify-content:center;}
  .lp-a-root .calendar{padding:16px;overflow:hidden;}
  .lp-a-root .team-cal .staff-row,.lp-a-root .team-cal .cal-body{grid-template-columns:30px repeat(5,1fr);gap:2px;}
  .lp-a-root .team-cal .avatar{width:22px;height:22px;font-size:9px;}
  .lp-a-root .team-cal .staff-name{font-size:9px;text-align:center;line-height:1.1;}
  .lp-a-root .team-cal .time-col .hour{font-size:9px;}
  .lp-a-root .team-cal .lane{height:260px;background-size:100% 52px;}
  .lp-a-root .team-cal .event{padding:3px 4px;}
  .lp-a-root .team-cal .event .ev-n{font-size:9px;}
  .lp-a-root .team-cal .event .ev-s{font-size:8px;}
  .lp-a-root .cal-head h3{font-size:15px;}
  .lp-a-root .book-toast{position:static;margin-top:14px;min-width:0;width:100%;animation:none;}
  .lp-a-root .proof{padding:24px 0;gap:16px;}
  .lp-a-root .proof .row{font-size:14px;gap:14px;}
  .lp-a-root .stats{padding:48px 0;gap:32px 20px;}
  .lp-a-root .stat .num{font-size:48px;margin-bottom:8px;}
  .lp-a-root .stat .desc{font-size:13px;}
  .lp-a-root .section-head{padding:40px 0 24px;gap:16px;}
  .lp-a-root .section-head h2{font-size:28px;}
  .lp-a-root .features{gap:16px;padding-bottom:48px;}
  .lp-a-root .feat{padding:22px;min-height:0;}
  .lp-a-root .feat h3{font-size:20px;}
  .lp-a-root .feat p{font-size:14px;}
  .lp-a-root .quote{padding:56px 0;}
  .lp-a-root .quote q{font-size:22px;line-height:1.3;}
  .lp-a-root .pricing-section{padding:48px 0;}
  .lp-a-root .pricing-inner{padding:0 20px;}
  .lp-a-root .pricing-head{margin-bottom:28px;}
  .lp-a-root .pricing-head h2{font-size:28px;}
  .lp-a-root .pricing-head p{font-size:15px;margin-top:10px;}
  .lp-a-root .final-cta{padding:56px 20px;}
  .lp-a-root .final-cta h2{font-size:28px;}
  .lp-a-root .final-cta p{font-size:15px;margin:14px auto 24px;}
  .lp-a-root .lp-footer{padding:36px 20px;gap:24px;}
  .lp-a-root .lp-footer ul{font-size:13px;}
  .lp-a-root .mobile-menu{padding:8px 20px 16px;}
  .lp-a-root .mobile-menu a,.lp-a-root .mobile-menu .m-solutions-trigger{padding:12px 8px;min-height:44px;}
  .lp-a-root .mobile-menu .m-lang [data-radix-select-trigger],.lp-a-root .mobile-menu .m-lang button[role="combobox"]{height:44px;width:100%;}
}
@media (max-width:380px){
  .lp-a-root h1.hero-h{font-size:30px;}
  .lp-a-root .lp-nav,.lp-a-root .wrap{padding-left:16px;padding-right:16px;}
  .lp-a-root .team-cal .staff-row,.lp-a-root .team-cal .cal-body{grid-template-columns:26px repeat(5,1fr);gap:1px;}
  .lp-a-root .team-cal .staff-name{display:none;}
  .lp-a-root .team-cal .event .ev-s{display:none;}
  .lp-a-root .team-cal .lane{height:240px;background-size:100% 48px;}
}
`;
